import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import type { ContentItem, ContentConfig, ContentResult } from '../schema';
import type { ContentStateData } from '../engine/types';

// ============================================
// State Types
// ============================================

export interface ContentContextState {
  // Content
  content: ContentItem | null;
  config: ContentConfig | null;
  
  // Interaction state
  contentState: ContentStateData;
  
  // Results
  results: ContentResult[];
  
  // UI state
  currentIndex: number;
  totalItems: number;
  isLoading: boolean;
  error: Error | null;
}

export type ContentAction =
  | { type: 'SET_CONTENT'; payload: ContentItem }
  | { type: 'SET_CONFIG'; payload: ContentConfig }
  | { type: 'SET_CONTENT_STATE'; payload: Partial<ContentStateData> }
  | { type: 'ADD_RESULT'; payload: ContentResult }
  | { type: 'UPDATE_RESULT'; payload: { contentId: string; updates: Partial<ContentResult> } }
  | { type: 'CLEAR_RESULTS' }
  | { type: 'SET_INDEX'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: Error | null }
  | { type: 'RESET' };

// ============================================
// Initial State
// ============================================

export const initialContentState: ContentContextState = {
  content: null,
  config: null,
  contentState: {
    state: 'idle',
    selectedAnswer: null,
    showFeedback: false,
    showHint: false,
    showExplanation: false,
    isLocked: false,
    isCorrect: null,
    score: 0,
    max: 1,
  },
  results: [],
  currentIndex: 0,
  totalItems: 0,
  isLoading: false,
  error: null,
};

// ============================================
// Reducer
// ============================================

export function contentReducer(
  state: ContentContextState,
  action: ContentAction
): ContentContextState {
  switch (action.type) {
    case 'SET_CONTENT':
      return {
        ...state,
        content: action.payload,
        contentState: { ...initialContentState.contentState },
      };

    case 'SET_CONFIG':
      return {
        ...state,
        config: action.payload,
      };

    case 'SET_CONTENT_STATE':
      return {
        ...state,
        contentState: { ...state.contentState, ...action.payload },
      };

    case 'ADD_RESULT': {
      const existingIndex = state.results.findIndex(
        (r) => r.contentId === action.payload.contentId
      );
      
      if (existingIndex >= 0) {
        const newResults = [...state.results];
        newResults[existingIndex] = action.payload;
        return { ...state, results: newResults };
      }
      
      return {
        ...state,
        results: [...state.results, action.payload],
      };
    }

    case 'UPDATE_RESULT': {
      const { contentId, updates } = action.payload;
      const results = state.results.map((r) =>
        r.contentId === contentId ? { ...r, ...updates } : r
      );
      return { ...state, results };
    }

    case 'CLEAR_RESULTS':
      return {
        ...state,
        results: [],
      };

    case 'SET_INDEX':
      return {
        ...state,
        currentIndex: action.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };

    case 'RESET':
      return initialContentState;

    default:
      return state;
  }
}

// ============================================
// Context
// ============================================

export interface ContentContextValue {
  state: ContentContextState;
  dispatch: React.Dispatch<ContentAction>;
  
  // Convenience actions
  setContent: (content: ContentItem) => void;
  setConfig: (config: ContentConfig) => void;
  setContentState: (state: Partial<ContentStateData>) => void;
  addResult: (result: ContentResult) => void;
  clearResults: () => void;
  setIndex: (index: number) => void;
  reset: () => void;
}

const ContentContext = createContext<ContentContextValue | null>(null);

// ============================================
// Provider
// ============================================

export interface ContentProviderProps {
  children: React.ReactNode;
  initialContent?: ContentItem;
  initialConfig?: ContentConfig;
}

export function ContentProvider({
  children,
  initialContent,
  initialConfig,
}: ContentProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(contentReducer, {
    ...initialContentState,
    content: initialContent ?? null,
    config: initialConfig ?? null,
  });

  // Convenience actions
  const setContent = useCallback((content: ContentItem) => {
    dispatch({ type: 'SET_CONTENT', payload: content });
  }, []);

  const setConfig = useCallback((config: ContentConfig) => {
    dispatch({ type: 'SET_CONFIG', payload: config });
  }, []);

  const setContentState = useCallback((contentState: Partial<ContentStateData>) => {
    dispatch({ type: 'SET_CONTENT_STATE', payload: contentState });
  }, []);

  const addResult = useCallback((result: ContentResult) => {
    dispatch({ type: 'ADD_RESULT', payload: result });
  }, []);

  const clearResults = useCallback(() => {
    dispatch({ type: 'CLEAR_RESULTS' });
  }, []);

  const setIndex = useCallback((index: number) => {
    dispatch({ type: 'SET_INDEX', payload: index });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const value = useMemo<ContentContextValue>(
    () => ({
      state,
      dispatch,
      setContent,
      setConfig,
      setContentState,
      addResult,
      clearResults,
      setIndex,
      reset,
    }),
    [state, setContent, setConfig, setContentState, addResult, clearResults, setIndex, reset]
  );

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useContentContext(): ContentContextValue {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContentContext must be used within a ContentProvider');
  }
  return context;
}

// Optional hook that returns null if not in provider
export function useContentContextOptional(): ContentContextValue | null {
  return useContext(ContentContext);
}
